import React, { useState, useEffect } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import FormInput from "../../components/CustomComponents/Input/formInput";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import "../../components/CustomComponents/Switchery/ToggleSwitch.css";
import "./twilioGatewayModal.scss";

/** Clearly fake sample values for placeholders / helper hints — never real credentials. */
const SAMPLE = {
  accountSid: "ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
  apiKeySid: "SKxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
  messagingServiceSid: "MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
  smsPhone: "+44XXXXXXXXXX",
  whatsAppSender: "whatsapp:+1XXXXXXXXXX",
  whatsAppSandbox: "whatsapp:+1XXXXXXXXXX",
};

const emptyForm = (compID) => ({
  ID: 0,
  UseServeIQTwilio: true,
  AccountSid: "",
  ApiKeySid: "",
  ApiKeySecret: "",
  MessagingServiceSid: "",
  SmsSender: "",
  WhatsAppSender: "",
  AuthToken: "",
  SmsEnabled: true,
  WhatsAppEnabled: false,
  Status: true,
  CompID: compID,
});

function FieldHint({ children, isDarkMode }) {
  return (
    <p
      className="twilio-gateway-modal__hint mb-0 mt-1"
      style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}
    >
      {children}
    </p>
  );
}

function SectionCard({ title, description, children, isDarkMode }) {
  return (
    <section
      className="twilio-gateway-modal__section"
      style={{
        background: isDarkMode ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
        borderColor: isDarkMode ? "#404040" : "#e9ecef",
      }}
    >
      <header className="twilio-gateway-modal__section-header">
        <h6
          className="twilio-gateway-modal__section-title mb-0"
          style={{ color: isDarkMode ? "#f8f9fa" : "#212529" }}
        >
          {title}
        </h6>
        {description ? (
          <p
            className="twilio-gateway-modal__section-desc mb-0"
            style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}
          >
            {description}
          </p>
        ) : null}
      </header>
      <div className="twilio-gateway-modal__section-body">{children}</div>
    </section>
  );
}

function AddTwilioGatewayModal({
  isModalOpen,
  setIsModalOpen,
  onTwilioGatewaySubmit,
  compID,
  gatewayToEdit = null,
  setTwilioGateways,
  platformKeysConfigured = false,
}) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(emptyForm(compID));

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const useServeIQ = formData.UseServeIQTwilio;

  useEffect(() => {
    if (!isModalOpen) return;

    if (gatewayToEdit) {
      setFormData({
        ID: gatewayToEdit.id ?? gatewayToEdit.ID ?? 0,
        UseServeIQTwilio:
          gatewayToEdit.useServeIQTwilio ?? gatewayToEdit.UseServeIQTwilio ?? true,
        AccountSid: gatewayToEdit.accountSid ?? gatewayToEdit.AccountSid ?? "",
        ApiKeySid: gatewayToEdit.apiKeySid ?? gatewayToEdit.ApiKeySid ?? "",
        ApiKeySecret: "",
        MessagingServiceSid:
          gatewayToEdit.messagingServiceSid ?? gatewayToEdit.MessagingServiceSid ?? "",
        SmsSender: gatewayToEdit.smsSender ?? gatewayToEdit.SmsSender ?? "",
        WhatsAppSender: gatewayToEdit.whatsAppSender ?? gatewayToEdit.WhatsAppSender ?? "",
        AuthToken: "",
        SmsEnabled: gatewayToEdit.smsEnabled ?? gatewayToEdit.SmsEnabled ?? true,
        WhatsAppEnabled:
          gatewayToEdit.whatsAppEnabled ?? gatewayToEdit.WhatsAppEnabled ?? false,
        Status: gatewayToEdit.status ?? gatewayToEdit.Status ?? true,
        CompID: compID,
      });
    } else {
      setFormData(emptyForm(compID));
    }
  }, [gatewayToEdit, compID, isModalOpen]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData(emptyForm(compID));
  };

  const handleToggle = (field) => {
    setFormData((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.AccountSid?.trim()) {
        showErrorToast(
          useServeIQ ? "Please enter your Twilio subaccount SID" : "Please enter Account SID"
        );
        return;
      }

      if (!useServeIQ) {
        if (!formData.ApiKeySid?.trim()) {
          showErrorToast("Please enter API Key SID");
          return;
        }
        if (!gatewayToEdit && !formData.ApiKeySecret?.trim()) {
          showErrorToast("Please enter API Key Secret");
          return;
        }
      } else if (formData.Status && !platformKeysConfigured) {
        showErrorToast(
          "ServeIQ platform Twilio keys are not configured yet. Use your own credentials or contact support."
        );
        return;
      }

      if (
        useServeIQ &&
        formData.Status &&
        !formData.AuthToken?.trim() &&
        !gatewayToEdit?.hasAuthToken
      ) {
        showErrorToast(
          "Subaccount Auth Token is required. Open Twilio Console for this subaccount and paste its Auth Token."
        );
        return;
      }

      if (formData.Status) {
        // WhatsApp UI is hidden for now — only require SMS as the active channel.
        if (!formData.SmsEnabled) {
          showErrorToast("Enable SMS when the gateway is active");
          return;
        }
        if (
          formData.SmsEnabled &&
          !formData.MessagingServiceSid?.trim() &&
          !formData.SmsSender?.trim()
        ) {
          showErrorToast(
            "Messaging Service SID or SMS phone number is required when SMS is enabled"
          );
          return;
        }
        // WhatsApp validation paused while WhatsApp section is hidden:
        // if (formData.WhatsAppEnabled && !formData.WhatsAppSender?.trim()) {
        //   showErrorToast("WhatsApp sender is required when WhatsApp is enabled");
        //   return;
        // }
      }

      setLoading(true);
      const payload = {
        ID: formData.ID,
        CompID: compID,
        UseServeIQTwilio: formData.UseServeIQTwilio,
        AccountSid: formData.AccountSid.trim(),
        ApiKeySid: useServeIQ ? null : formData.ApiKeySid?.trim() || null,
        ApiKeySecret: useServeIQ ? null : formData.ApiKeySecret?.trim() ?? "",
        MessagingServiceSid: formData.MessagingServiceSid?.trim() || null,
        SmsSender: formData.SmsSender?.trim() || null,
        WhatsAppSender: formData.WhatsAppSender?.trim() || null,
        SmsEnabled: formData.SmsEnabled,
        WhatsAppEnabled: formData.WhatsAppEnabled,
        Status: formData.Status,
        AuthToken: formData.AuthToken?.trim() || null,
      };

      let response;
      if (gatewayToEdit) {
        response = await axiosInstance.put(APIs.UPDATE_TWILIO_GATEWAY, payload);
        if (response.data.status) {
          setTwilioGateways([response.data.data]);
        }
      } else {
        response = await axiosInstance.post(APIs.POST_ADD_TWILIO_GATEWAY, payload);
        if (response.data.status) {
          setTwilioGateways([response.data.data]);
        }
      }

      if (response.data.status) {
        showSuccessToast(
          `Twilio settings ${gatewayToEdit ? "updated" : "added"} successfully`
        );
        onTwilioGatewaySubmit?.(response.data.data, !!gatewayToEdit);
        handleCloseModal();
      } else {
        showErrorToast(response.data?.message || "Could not save Twilio settings");
      }
    } catch (err) {
      showErrorToast(
        err.response?.data?.message ||
          `Failed to ${gatewayToEdit ? "update" : "add"} Twilio settings`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const secretPlaceholder = gatewayToEdit?.hasApiKeySecret
    ? "Leave blank to keep existing secret"
    : "e.g. your API key secret";
  const authTokenPlaceholder = gatewayToEdit?.hasAuthToken
    ? "Leave blank to keep existing auth token"
    : "e.g. your Twilio Auth Token";

  const labelColor = isDarkMode ? "#ffffff" : "#000000";

  return (
    <CustomModal
      isOpen={isModalOpen}
      onClose={handleCloseModal}
      onSubmit={handleSubmit}
      title={gatewayToEdit ? "Update Twilio Settings" : "Add Twilio Settings"}
      subtitle="Connect SMS and WhatsApp. Placeholders show sample format only — not real credentials."
      showButton={true}
      isCentered={true}
      modalSize="lg"
      maxHeight="85vh"
      minHeight="320px"
      submitButtonText={gatewayToEdit ? "Update" : "Add"}
      themeColor={themeColor}
    >
      <GridSectionLoader loading={loading} minHeight="200px">
        <div className="twilio-gateway-modal">
          <SectionCard
            title="Account"
            description="Choose ServeIQ-managed keys or your own Twilio credentials."
            isDarkMode={isDarkMode}
          >
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label" style={{ color: labelColor }}>
                  Credential source
                </label>
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <ToggleSwitch
                    isChecked={formData.UseServeIQTwilio}
                    onToggle={() => handleToggle("UseServeIQTwilio")}
                    label={
                      formData.UseServeIQTwilio
                        ? "ServeIQ platform keys"
                        : "My own Twilio keys"
                    }
                    disabled={false}
                  />
                </div>
                {useServeIQ && !platformKeysConfigured && (
                  <p className="small text-warning mb-0 mt-2">
                    ServeIQ platform API keys are not configured yet. Save as inactive or use your
                    own credentials.
                  </p>
                )}
              </div>

              <div className="col-12">
                <FormInput
                  label={useServeIQ ? "Subaccount SID" : "Account SID"}
                  name="AccountSid"
                  type="text"
                  value={formData.AccountSid}
                  placeHolder={`e.g. ${SAMPLE.accountSid}`}
                  onchange={handleInputChange}
                  required
                />
                <FieldHint isDarkMode={isDarkMode}>
                  {useServeIQ
                    ? "Twilio subaccount for this restaurant. API keys come from ServeIQ platform settings."
                    : "From Twilio Console → Account → Account SID."}
                </FieldHint>
              </div>

              {!useServeIQ && (
                <>
                  <div className="col-md-6">
                    <FormInput
                      label="API Key SID"
                      name="ApiKeySid"
                      type="text"
                      value={formData.ApiKeySid}
                      placeHolder={`e.g. ${SAMPLE.apiKeySid}`}
                      onchange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <FormInput
                      label="API Key Secret"
                      name="ApiKeySecret"
                      type="password"
                      value={formData.ApiKeySecret}
                      placeHolder={secretPlaceholder}
                      onchange={handleInputChange}
                      required={!gatewayToEdit}
                    />
                    {gatewayToEdit?.apiKeySecretMasked && (
                      <FieldHint isDarkMode={isDarkMode}>
                        Current secret: {gatewayToEdit.apiKeySecretMasked}
                      </FieldHint>
                    )}
                  </div>
                </>
              )}

              <div className="col-12">
                <FormInput
                  label={
                    useServeIQ
                      ? "Subaccount Auth Token (required)"
                      : "Auth Token (inbound webhooks)"
                  }
                  name="AuthToken"
                  type="password"
                  value={formData.AuthToken}
                  placeHolder={authTokenPlaceholder}
                  onchange={handleInputChange}
                />
                <FieldHint isDarkMode={isDarkMode}>
                  {useServeIQ
                    ? "Required for sending SMS. In Twilio Console, open this restaurant's subaccount and copy its Auth Token (not the parent account token)."
                    : "Needed for two-way SMS/WhatsApp. Use the Auth Token from Twilio Console for this account."}
                  {gatewayToEdit?.authTokenMasked
                    ? ` Current: ${gatewayToEdit.authTokenMasked}`
                    : ""}
                </FieldHint>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title="SMS"
            description="Prefer Messaging Service for production. Phone number is a fallback only."
            isDarkMode={isDarkMode}
          >
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label" style={{ color: labelColor }}>
                  SMS Enabled
                </label>
                <ToggleSwitch
                  isChecked={formData.SmsEnabled}
                  onToggle={() => handleToggle("SmsEnabled")}
                  label={formData.SmsEnabled ? "On" : "Off"}
                  disabled={false}
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Messaging Service SID"
                  name="MessagingServiceSid"
                  type="text"
                  value={formData.MessagingServiceSid}
                  placeHolder={`e.g. ${SAMPLE.messagingServiceSid}`}
                  onchange={handleInputChange}
                />
                <FieldHint isDarkMode={isDarkMode}>
                  Sample format only. Your Twilio number must be in the sender pool.
                </FieldHint>
              </div>
              <div className="col-md-6">
                <FormInput
                  label="SMS phone number (From)"
                  name="SmsSender"
                  type="text"
                  value={formData.SmsSender}
                  placeHolder={`e.g. ${SAMPLE.smsPhone}`}
                  onchange={handleInputChange}
                />
                <FieldHint isDarkMode={isDarkMode}>
                  E.164 format hint. Leave blank if you only use Messaging Service.
                </FieldHint>
                {formData.MessagingServiceSid?.trim() && formData.SmsSender?.trim() && (
                  <p className="small text-info mb-0 mt-1">
                    Both set — Messaging Service SID is used for sending.
                  </p>
                )}
              </div>
            </div>
          </SectionCard>

          {/* WhatsApp section hidden for now — re-enable when WhatsApp channel is ready.
          <SectionCard
            title="WhatsApp"
            description="Sender must use the whatsapp: prefix. Different from SMS From number."
            isDarkMode={isDarkMode}
          >
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label" style={{ color: labelColor }}>
                  WhatsApp Enabled
                </label>
                <ToggleSwitch
                  isChecked={formData.WhatsAppEnabled}
                  onToggle={() => handleToggle("WhatsAppEnabled")}
                  label={formData.WhatsAppEnabled ? "On" : "Off"}
                  disabled={false}
                />
              </div>
              <div className="col-12">
                <FormInput
                  label="WhatsApp sender"
                  name="WhatsAppSender"
                  type="text"
                  value={formData.WhatsAppSender}
                  placeHolder={`e.g. ${SAMPLE.whatsAppSender}`}
                  onchange={handleInputChange}
                />
                <FieldHint isDarkMode={isDarkMode}>
                  Sample: <code>{SAMPLE.whatsAppSandbox}</code>. For sandbox testing, join the
                  Twilio WhatsApp sandbox in Console, then paste your assigned sender here.
                </FieldHint>
              </div>
            </div>
          </SectionCard>
          */}

          <SectionCard
            title="Status"
            description="Inactive gateways stay saved but do not send messages."
            isDarkMode={isDarkMode}
          >
            <label className="form-label" style={{ color: labelColor }}>
              Gateway status
            </label>
            <ToggleSwitch
              isChecked={formData.Status}
              onToggle={() => handleToggle("Status")}
              label={formData.Status ? "Active" : "Inactive"}
              disabled={false}
            />
          </SectionCard>
        </div>
      </GridSectionLoader>
    </CustomModal>
  );
}

export default AddTwilioGatewayModal;
